import { useEffect, useMemo, useState } from "react";

import { MinusCircleIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Pagination from "src/components/ui/table/Pagination";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import { usePagination } from "src/hooks/usePagination";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { FiveWayType } from "./types";
import { FiveWhyType } from "src/api/config/systemSwitch";

export type FiveWhyAndDeviationTableHeadType = "Type" | "Title" | "CreatedDate" | "ActionCount" | "InstrumentSDCACount";

const tableHeaderContent: Record<FiveWhyAndDeviationTableHeadType, TableColumnWithIcon> = {
  Type: {
    title: "Тип формы",
    sort: true,
    style: { width: "10%" },
  },
  Title: {
    title: "Название",
    sort: false,
    style: { width: "50%" },
  },
  CreatedDate: {
    title: "Дата создания",
    sort: true,
    style: { width: "15%" },
  },
  ActionCount: {
    title: "Количество действий",
    sort: false,
    style: { width: "10%" },
  },
  InstrumentSDCACount: {
    title: "Количество SDCA инструментов",
    sort: false,
    style: { width: "15%" },
  },
};

function FiveWhyAndDeviationModalTable({
  list,
  loading,
}: {
  list: (Record<FiveWhyAndDeviationTableHeadType | "ID", any> & { ID: number })[];
  loading: boolean;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>("CreatedDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return list;
    }

    const sortedData = [...list];

    const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

    sortedData.sort((a, b) => {
      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (sortedField === "CreatedDate") {
        const dateA = moment(a[sortedField], "DD.MM.YYYY");
        const dateB = moment(b[sortedField], "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB, locales, options)
          : valueB.localeCompare(valueA, locales, options);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, list]) as (Record<FiveWhyAndDeviationTableHeadType, any> & {
    ID: number;
    item: FiveWayType;
  })[];

  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          loading={loading}
          head={tableHeaderContent}
          body={sortedTableBodyContent}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
        />
      </FlexCol>
    </FlexRow>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  loading,
}: {
  head: Record<keyof typeof tableHeaderContent, TableColumnWithIcon>;
  body: (Record<FiveWhyAndDeviationTableHeadType, any> & { ID: number; item: FiveWayType })[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
  loading: boolean;
}) {
  const [query, setQuery] = useSearchParams();

  function openFiveWhyCard(type: string, id: number | string) {
    if (type === FiveWhyType) {
      const newSearchParams = new URLSearchParams(query);
      newSearchParams.set("5WayCard", id.toString());
      setQuery(newSearchParams);
    } else {
      const newSearchParams = new URLSearchParams(query);
      newSearchParams.set("deviationCard", id.toString());
      setQuery(newSearchParams);
    }
  }

  return (
    <FlexRow className="pb-5 max-h-[365px] overflow-auto relative bg-white rounded-lg table-custom shadow-custom scrollbar-tg">
      <FlexCol className="divide-y min-w-[970px] grow min-h-fit">
        <FlexRow className="sticky top-0 px-6 py-5 bg-white gap-x-4">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as keyof typeof tableHeaderContent;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {!loading ? (
          Array.isArray(body) && body.length > 0 ? (
            body.map((item) => (
              <FlexRow className="py-4 px-6  hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg gap-x-4" key={item.ID}>
                {/* Дата создания */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {item.Type}
                </FlexCol>
                {/* Название совещания */}
                <FlexCol
                  className="break-words cursor-pointer text-darkblue"
                  style={{ width: "50%" }}
                  onClick={() => openFiveWhyCard(item.item.Type5Way, item.ID)}
                >
                  {item.Title}
                </FlexCol>

                {/* Дата создания */}
                <FlexCol className="break-words" style={{ width: "15%" }}>
                  {moment(item.CreatedDate).format("DD.MM.YYYY")}
                </FlexCol>

                {/* Локация */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {item.ActionCount}
                </FlexCol>

                {/* Отдел */}
                <FlexCol className="break-words" style={{ width: "15%" }}>
                  {item.InstrumentSDCACount}
                </FlexCol>
              </FlexRow>
            ))
          ) : (
            <EmptyList
              icon={<MinusCircleIcon width={24} height={24} color="#637381" />}
              title={`Нет 5почему и Отчетов об отклонении`}
            />
          )
        ) : (
          <div className="pt-4">
            <Loading />
          </div>
        )}
      </FlexCol>
    </FlexRow>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="items-center gap-x-2">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium text-black whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

export default FiveWhyAndDeviationModalTable;
